import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { createElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import escapeRegExp from "lodash/escapeRegExp";
import useAutocomplete, { createFilterOptions } from "@mui/material/useAutocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Popper from "@mui/material/Popper";
import FormControl from "@mui/material/FormControl";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { Box } from "../../Box/index.js";
import { Icon } from "../../Icon/index.js";
import { Label, Preloader } from "../common/index.js";
import { Paper } from "../../Paper/index.js";
import { TreeView } from "../../TreeView/index.js";
import { useIsInsideContainer } from "../../hooks/index.js";
import { Typography } from "../../Typography/index.js";
import { CircularProgress } from "../../CircularProgress/index.js";
import { ListBox } from "./ListBox.js";
import { useStyles } from "./TreeViewAutocomplete.styles.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function TreeViewAutocomplete_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else TreeViewAutocomplete_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
function _object_without_properties(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys, key, i;
    if ("u" > typeof Reflect && Reflect.ownKeys) {
        sourceKeys = Reflect.ownKeys(Object(source));
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
        return target;
    }
    target = _object_without_properties_loose(source, excluded);
    if (Object.getOwnPropertySymbols) {
        sourceKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (!(excluded.indexOf(key) >= 0)) {
            if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
        }
    }
    return target;
}
const MIN_INPUT_VALUE_LENGTH = 3;
const TreeViewAutocomplete = (_0)=>{
    let { id, disabled = false, label, labelIcon, tooltip, options, placeholder = '', clearable = true, required = false, getOptionLabel = (option)=>option.label || '', noOptionsText = 'Ничего не найдено', onChange, onBlur, onSearch, onChildrenLoad, onLoadTree, value, error = false, helperText = '', loading = false, testId = 'test-id-tree-view-autocomplete', limit = 50, withoutMargins = false, renderSearchOption, renderOption } = _0, rest = _object_without_properties(_0, [
        "id",
        "disabled",
        "label",
        "labelIcon",
        "tooltip",
        "options",
        "placeholder",
        "clearable",
        "required",
        "getOptionLabel",
        "noOptionsText",
        "onChange",
        "onBlur",
        "onSearch",
        "onChildrenLoad",
        "onLoadTree",
        "value",
        "error",
        "helperText",
        "loading",
        "testId",
        "limit",
        "withoutMargins",
        "renderSearchOption",
        "renderOption"
    ]);
    const { classes } = useStyles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const [isLoading, setLoading] = useState(false);
    const [isPaginationLoading, setPaginationLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [unitTree, setUnitTree] = useState(options);
    const [loadedOptions, setLoadedOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [cachedValue, setCachedValue] = useState('');
    const filterRef = useRef({
        limit,
        offset: 0,
        query: ''
    });
    const isInsideContainer = useIsInsideContainer();
    const isDisabled = disabled || loading;
    const treeOpen = !isDisabled && open;
    useEffect(()=>{
        if (treeOpen && onLoadTree && value) {
            setLoading(true);
            onLoadTree(value).then((result)=>{
                setUnitTree(result);
                setLoading(false);
            }).catch(()=>setLoading(false));
        }
    }, [
        onLoadTree,
        treeOpen,
        value
    ]);
    const handleChange = useCallback((nextValue, item)=>{
        onChange(nextValue, item);
        setOpen(false);
    }, [
        onChange
    ]);
    const handleClearClick = useCallback((e)=>{
        if (!focused) {
            e.preventDefault();
            e.stopPropagation();
        }
        setInputValue('');
        setCachedValue('');
        onChange('');
    }, [
        focused,
        onChange
    ]);
    const getFlatTree = useCallback((array)=>{
        let result = [];
        array.forEach((item)=>{
            if (item.children instanceof Array) {
                const { children } = item, itemRest = _object_without_properties(item, [
                    "children"
                ]);
                result.push(itemRest);
                result = [
                    ...result,
                    ...getFlatTree(children)
                ];
            } else result.push(item);
        });
        return result;
    }, []);
    const flatOptions = useMemo(()=>getFlatTree(options).sort((a, b)=>a.label > b.label ? 1 : -1), [
        getFlatTree,
        options
    ]);
    useEffect(()=>{
        setUnitTree(options);
    }, [
        options
    ]);
    useEffect(()=>{
        if (value) {
            const foundOption = flatOptions.find((opt)=>opt.id === value);
            if (foundOption) {
                setInputValue(foundOption.label);
                setCachedValue(foundOption.label);
            } else if (cachedValue) setInputValue(cachedValue);
            else {
                setInputValue(value);
                setCachedValue(value);
            }
        }
    }, [
        value,
        flatOptions,
        cachedValue
    ]);
    const handleSearch = useCallback((newValue)=>{
        if (newValue && onSearch) {
            setLoading(true);
            filterRef.current = {
                offset: 0,
                limit: 50,
                query: ''
            };
            setHasMore(true);
            onSearch({
                query: newValue,
                limit: 50,
                offset: 0
            }).then((result)=>{
                setLoadedOptions(result);
                setHasMore(result.length >= limit);
                setLoading(false);
            }).catch(()=>setLoading(false));
        }
    }, [
        onSearch,
        limit
    ]);
    useEffect(()=>{
        if (!value) {
            setInputValue('');
            setCachedValue('');
            setLoadedOptions([]);
        }
    }, [
        value
    ]);
    const { getRootProps, getInputProps, groupedOptions, getOptionProps } = useAutocomplete({
        id: inputId,
        options: onSearch ? loadedOptions : flatOptions,
        value: value || null,
        clearOnBlur: false,
        getOptionLabel,
        blurOnSelect: true,
        isOptionEqualToValue: (option)=>option.id === value,
        disabled: isDisabled,
        inputValue,
        onChange: (event, val)=>{
            if (val && onLoadTree) {
                setLoading(true);
                onLoadTree(val.id).then((result)=>{
                    setUnitTree(result);
                    const unitsFounded = getFlatTree(result);
                    const unitFound = unitsFounded.find((unit)=>unit.id === val.id);
                    if (null == unitFound ? void 0 : unitFound.disabled) return;
                    handleChange(val.id, null != unitFound ? unitFound : val);
                    setInputValue(val.label);
                    setCachedValue(val.label);
                }).catch(()=>{
                    handleChange(val ? val.id : '', null != val ? val : void 0);
                    setInputValue(val ? val.label : '');
                    setCachedValue(val ? val.label : '');
                }).finally(()=>{
                    setLoading(false);
                });
            } else {
                handleChange(val ? val.id : '', null != val ? val : void 0);
                setInputValue(val ? val.label : '');
                setCachedValue(val ? val.label : '');
            }
        },
        filterOptions: createFilterOptions({
            ignoreAccents: false
        }),
        onInputChange: (event, nextValue, reason)=>{
            if ('reset' !== reason) {
                null == handleSearch || handleSearch(nextValue);
                setInputValue(nextValue);
            }
            if ('' !== nextValue) setOpen(true);
        }
    });
    const popperRef = useRef(null);
    const textInputRef = useRef(null);
    const isPopperOpen = treeOpen && (!inputValue || inputValue.length >= MIN_INPUT_VALUE_LENGTH);
    const getHighlightedLabel = useCallback((currentValue)=>{
        const words = currentValue.split(new RegExp(`(${escapeRegExp(inputValue)})`, 'gi'));
        return /*#__PURE__*/ jsx("span", {
            children: words.map((part, i)=>/*#__PURE__*/ jsx(Typography, {
                    className: classes.optionText,
                    component: "span",
                    color: part.toLowerCase() === inputValue.toLowerCase() ? 'text.light.primary' : 'text.light.tertirary',
                    children: part
                }, i))
        });
    }, [
        inputValue
    ]);
    const handleNext = ()=>{
        if (!onSearch || isPaginationLoading) return;
        const newFilter = _object_spread_props(_object_spread({}, filterRef.current), {
            query: inputValue,
            offset: filterRef.current.offset + filterRef.current.limit
        });
        filterRef.current = newFilter;
        setPaginationLoading(true);
        onSearch(newFilter).then((result)=>{
            setLoadedOptions((prev)=>[
                    ...prev,
                    ...result
                ]);
            setPaginationLoading(false);
            setHasMore(result.length >= newFilter.limit);
        }).catch(()=>setPaginationLoading(false));
    };
    const onFocus = useCallback(()=>{
        setFocused(true);
        setInputValue('');
        setCachedValue(inputValue);
    }, [
        inputValue
    ]);
    const handlePopperOutsideClick = useCallback((event)=>{
        var _popperRef_current, _textInputRef_current_parentNode, _textInputRef_current, _textInputRef_current1;
        const target = event.target;
        const isTargetInsidePopper = null == popperRef ? void 0 : null == (_popperRef_current = popperRef.current) ? void 0 : _popperRef_current.contains(target);
        const wasTextFieldClicked = (null == textInputRef ? void 0 : null == (_textInputRef_current = textInputRef.current) ? void 0 : null == (_textInputRef_current_parentNode = _textInputRef_current.parentNode) ? void 0 : _textInputRef_current_parentNode.contains(target)) || (null == textInputRef ? void 0 : null == (_textInputRef_current1 = textInputRef.current) ? void 0 : _textInputRef_current1.contains(target));
        if (!wasTextFieldClicked && open && !isTargetInsidePopper && !isInsideContainer) setOpen(false);
    }, [
        open,
        popperRef,
        isInsideContainer
    ]);
    const handlePopperMouseDown = useCallback((event)=>{
        event.preventDefault();
    }, []);
    const onClick = useCallback(()=>{
        setOpen(true);
        setFocused(true);
    }, []);
    const onBlurInner = useCallback((event)=>{
        var _popperRef_current;
        null == onBlur || onBlur(event);
        const { relatedTarget } = event;
        const containsTarget = null == popperRef ? void 0 : null == (_popperRef_current = popperRef.current) ? void 0 : _popperRef_current.contains(relatedTarget);
        if (!containsTarget) setOpen(false);
        setFocused(false);
        setInputValue(cachedValue);
    }, [
        cachedValue,
        onBlur
    ]);
    const onTreeViewSelect = useCallback((valueId, newLabel, item)=>{
        handleChange(valueId, item);
        setInputValue(newLabel || '');
        setCachedValue(newLabel || '');
        setTimeout(()=>{
            var _textInputRef_current;
            null == (_textInputRef_current = textInputRef.current) || _textInputRef_current.blur();
        }, 0);
    }, [
        handleChange,
        textInputRef
    ]);
    const isShowNoOptionText = !isLoading && 0 === groupedOptions.length;
    const isShowMenuOptions = !isLoading && 0 !== groupedOptions.length;
    return /*#__PURE__*/ jsxs("div", _object_spread_props(_object_spread({}, getRootProps()), {
        children: [
            /*#__PURE__*/ jsxs(FormControl, {
                className: classnames(classes.treeViewAutocompleteInput, withoutMargins && classes.withoutMargins, 'aqa_tree_view_autocomplete_input'),
                children: [
                    label && /*#__PURE__*/ jsx(Label, {
                        label: label,
                        required: required,
                        tooltip: tooltip,
                        icon: labelIcon,
                        inputId: inputId
                    }),
                    /*#__PURE__*/ jsx(TextField, _object_spread_props(_object_spread({
                        id: inputId,
                        "data-testid": testId
                    }, rest), {
                        disabled: isDisabled,
                        fullWidth: true,
                        variant: "outlined",
                        autoComplete: "off",
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label,
                        onFocus: onFocus,
                        onClick: onClick,
                        value: loading ? 'Загрузка...' : inputValue,
                        onBlur: onBlurInner,
                        inputRef: textInputRef,
                        slotProps: {
                            htmlInput: _object_spread({
                                className: classes.input
                            }, getInputProps()),
                            input: {
                                ref: setAnchorEl,
                                className: `${classes.inputRoot} aqa_select`,
                                disabled: isDisabled,
                                endAdornment: loading ? /*#__PURE__*/ jsx(Preloader, {}) : /*#__PURE__*/ jsxs(InputAdornment, {
                                    className: classes.endAdornment,
                                    position: "end",
                                    children: [
                                        inputValue && (focused || clearable) && /*#__PURE__*/ jsx(Box, {
                                            className: classes.clearButton,
                                            onClick: handleClearClick,
                                            children: /*#__PURE__*/ jsx(Icon, {
                                                name: "dismiss_substract",
                                                color: "stroke.secondary",
                                                size: "24"
                                            })
                                        }),
                                        /*#__PURE__*/ jsx(Box, {
                                            className: classes.arrowButton,
                                            children: /*#__PURE__*/ jsx(Box, {
                                                m: "4",
                                                className: classnames({
                                                    [classes.popupIndicatorOpen]: treeOpen
                                                }),
                                                children: /*#__PURE__*/ jsx(Icon, {
                                                    name: "arrow_triangle_down",
                                                    size: "16",
                                                    color: "text.dark.secondary"
                                                })
                                            })
                                        })
                                    ]
                                })
                            }
                        }
                    }))
                ]
            }),
            /*#__PURE__*/ jsx(ClickAwayListener, {
                onClickAway: handlePopperOutsideClick,
                children: /*#__PURE__*/ jsx(Popper, {
                    role: "presentation",
                    open: isPopperOpen,
                    ref: popperRef,
                    anchorEl: anchorEl,
                    style: {
                        width: anchorEl ? anchorEl.clientWidth : 'auto',
                        zIndex: 1400
                    },
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [
                                    0,
                                    5
                                ]
                            }
                        }
                    ],
                    onMouseDown: handlePopperMouseDown,
                    children: /*#__PURE__*/ jsx(Paper, {
                        elevation: 2,
                        className: classes.paper,
                        children: /*#__PURE__*/ jsx(Box, {
                            py: "8",
                            children: inputValue ? /*#__PURE__*/ jsxs(Fragment, {
                                children: [
                                    isLoading && /*#__PURE__*/ jsx(Box, {
                                        display: "flex",
                                        justifyContent: "center",
                                        p: "16",
                                        children: /*#__PURE__*/ jsx(CircularProgress, {
                                            size: 30
                                        })
                                    }),
                                    isShowMenuOptions && /*#__PURE__*/ jsx(ListBox, {
                                        hasMore: hasMore,
                                        dataLength: groupedOptions.length,
                                        next: handleNext,
                                        loading: isPaginationLoading,
                                        children: groupedOptions.map((option, index)=>{
                                            const highlightedLabel = getHighlightedLabel(getOptionLabel(option));
                                            return /*#__PURE__*/ createElement(MenuItem, _object_spread_props(_object_spread({
                                                className: classes.menuItem
                                            }, getOptionProps({
                                                option,
                                                index
                                            })), {
                                                key: option.id
                                            }), renderSearchOption ? renderSearchOption(option, highlightedLabel) : highlightedLabel);
                                        })
                                    }),
                                    isShowNoOptionText && /*#__PURE__*/ jsx(Box, {
                                        className: classes.menuItem,
                                        children: /*#__PURE__*/ jsx(Typography, {
                                            color: "text.light.tertirary",
                                            className: classes.optionText,
                                            children: noOptionsText
                                        })
                                    })
                                ]
                            }) : /*#__PURE__*/ jsxs(Fragment, {
                                children: [
                                    isLoading && /*#__PURE__*/ jsx(Box, {
                                        display: "flex",
                                        justifyContent: "center",
                                        p: "16",
                                        children: /*#__PURE__*/ jsx(CircularProgress, {
                                            size: 30
                                        })
                                    }),
                                    !isLoading && /*#__PURE__*/ jsx(TreeView, {
                                        items: unitTree,
                                        selected: value || '',
                                        selectOnShrink: false,
                                        showRulers: false,
                                        showCounters: false,
                                        onSelect: onTreeViewSelect,
                                        onChildrenLoad: onChildrenLoad,
                                        renderOption: renderOption
                                    })
                                ]
                            })
                        })
                    })
                })
            })
        ]
    }));
};
export { TreeViewAutocomplete };
